/**
 * Utilities for managing paywall-related persistent storage
 */

interface NagBarDismissal {
  context: string
  dismissedAt: number
}

interface FomoCounters {
  missedMeetings: number
  missedHighlights: number
  lastResetAt: number
}

interface DismissalTracking {
  count: number
  isPermanent: boolean
  permanentAt?: number
}

const NAG_BAR_STORAGE_KEY = 'highlight-nagbar-dismissals'
const FOMO_COUNTERS_KEY = 'highlight-fomo-counters'
const NAG_BAR_DISMISSAL_TRACKING_KEY = 'highlight-nagbar-tracking'
const DISMISSAL_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
const MAX_DISMISSALS_BEFORE_PERMANENT = 5

/**
 * Get all nag bar dismissals from storage
 */
export const getNagBarDismissals = (): Record<string, number> => {
  try {
    const stored = localStorage.getItem(NAG_BAR_STORAGE_KEY)
    if (!stored) return {}

    const dismissals: NagBarDismissal[] = JSON.parse(stored)
    const now = Date.now()

    // Filter out expired dismissals and convert to record
    const valid = dismissals
      .filter((d) => now - d.dismissedAt < DISMISSAL_EXPIRY_MS)
      .reduce(
        (acc, d) => {
          acc[d.context] = d.dismissedAt
          return acc
        },
        {} as Record<string, number>,
      )

    return valid
  } catch (error) {
    console.error('Failed to read nag bar dismissals:', error)
    return {}
  }
}

export const clearAllNagBarDismissals = (): void => {
  try {
    localStorage.removeItem(NAG_BAR_STORAGE_KEY)
    localStorage.removeItem(NAG_BAR_DISMISSAL_TRACKING_KEY)
  } catch (error) {
    console.error('Failed to clear all nag bar dismissals:', error)
  }
}

/**
 * Get dismissal tracking data
 */
export const getDismissalTracking = (): DismissalTracking => {
  try {
    const stored = localStorage.getItem(NAG_BAR_DISMISSAL_TRACKING_KEY)
    if (!stored) {
      return { count: 0, isPermanent: false }
    }
    const tracking: DismissalTracking = JSON.parse(stored)

    // Check if permanent dismissal has expired (30 days)
    if (tracking.isPermanent && tracking.permanentAt) {
      const now = Date.now()
      if (now - tracking.permanentAt >= DISMISSAL_EXPIRY_MS) {
        // Reset after 30 days
        return { count: 0, isPermanent: false }
      }
    }

    return tracking
  } catch (error) {
    console.error('Failed to read dismissal tracking:', error)
    return { count: 0, isPermanent: false }
  }
}

/**
 * Check if nag bars are permanently dismissed
 */
export const isPermanentlyDismissed = (): boolean => {
  const tracking = getDismissalTracking()
  return tracking.isPermanent
}

/**
 * Increment nag bar dismissal count
 */
export const incrementNagBarDismissalCount = (): void => {
  try {
    const tracking = getDismissalTracking()

    // If already permanent, don't increment
    if (tracking.isPermanent) return

    const newCount = tracking.count + 1
    const isPermanent = newCount >= MAX_DISMISSALS_BEFORE_PERMANENT

    const newTracking: DismissalTracking = {
      count: newCount,
      isPermanent,
      permanentAt: isPermanent ? Date.now() : undefined,
    }

    localStorage.setItem(NAG_BAR_DISMISSAL_TRACKING_KEY, JSON.stringify(newTracking))
  } catch (error) {
    console.error('Failed to increment dismissal count:', error)
  }
}

/**
 * Check if a nag bar is currently dismissed
 */
export const isNagBarDismissed = (context: string): boolean => {
  // Check permanent dismissal first
  if (isPermanentlyDismissed()) return true

  const dismissals = getNagBarDismissals()
  if (!dismissals[context]) return false

  const now = Date.now()
  return now - dismissals[context] < DISMISSAL_EXPIRY_MS
}

/**
 * Dismiss a nag bar and track dismissal count
 */
export const dismissNagBar = (context: string): void => {
  try {
    // Increment global dismissal count
    incrementNagBarDismissalCount()

    const dismissals = getNagBarDismissals()
    dismissals[context] = Date.now()

    const dismissalArray: NagBarDismissal[] = Object.entries(dismissals).map(([ctx, time]) => ({
      context: ctx,
      dismissedAt: time,
    }))

    localStorage.setItem(NAG_BAR_STORAGE_KEY, JSON.stringify(dismissalArray))
  } catch (error) {
    console.error('Failed to dismiss nag bar:', error)
  }
}

/**
 * Clear expired dismissals
 */
export const clearExpiredDismissals = (): void => {
  try {
    const dismissals = getNagBarDismissals()
    const dismissalArray: NagBarDismissal[] = Object.entries(dismissals).map(([context, dismissedAt]) => ({
      context,
      dismissedAt,
    }))

    localStorage.setItem(NAG_BAR_STORAGE_KEY, JSON.stringify(dismissalArray))
  } catch (error) {
    console.error('Failed to clear expired dismissals:', error)
  }
}

/**
 * Get FOMO counters (missed meetings and highlights)
 */
export const getFomoCounters = (): FomoCounters => {
  try {
    const stored = localStorage.getItem(FOMO_COUNTERS_KEY)
    if (!stored) {
      return {
        missedMeetings: 0,
        missedHighlights: 0,
        lastResetAt: Date.now(),
      }
    }

    return JSON.parse(stored)
  } catch (error) {
    console.error('Failed to read FOMO counters:', error)
    return {
      missedMeetings: 0,
      missedHighlights: 0,
      lastResetAt: Date.now(),
    }
  }
}
